import React from 'react'
import { faFileArrowUp, faRotateLeft } from '@fortawesome/free-solid-svg-icons'
import {
  colors,
  DcButton,
  DcDiv,
  DcFlexBox,
  DcText,
  PixelFactoryContext,
  PixelIcon,
} from 'pixel-base'
import type { AjaxError, AjaxResponse } from 'rxjs/ajax'
import styled from 'styled-components'
import { submitBulkProductUpload } from 'utils/api-routes-planning'
import type { BulkUploadResponse } from 'utils/api-routes-planning'
import * as XLSX from 'xlsx'

import { IPixelFactory } from '../../../../../../decl.d'

export interface BulkProductUploadResult {
  productSku: string
  productName: string
  productImage?: string
  productType?: string
  stockCount?: number
  price?: number
}

type SheetRow = Record<
  string,
  string | number | boolean | Date | null | undefined
>

interface BulkProductUploadProps {
  parsedProducts?: BulkProductUploadResult[]
  onProductsParsed?: (products: BulkProductUploadResult[]) => void
  onClose?: () => void
}

const DropZone = styled.label<{ $isDragging: boolean }>`
  width: 100%;
  border: 1px dashed
    ${({ $isDragging }) => ($isDragging ? '#5c4dff' : '#dcdfe6')};
  border-radius: 12px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
  cursor: pointer;
  background-color: ${({ $isDragging }) =>
    $isDragging ? 'rgba(92,77,255,0.05)' : 'transparent'};
  transition: all 0.2s ease-in-out;
`

const PreviewList = styled.div`
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #f0f1f5;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const PreviewRow = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #f6f7fb;
  padding-bottom: 10px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const ErrorText = styled.span`
  color: #d93025;
  font-size: 13px;
`

const ACCEPTED_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

const normalizeKey = (value: string) =>
  value.replace(/[\s_-]/g, '').toLowerCase()

const extractNumber = (
  row: SheetRow,
  matcherKeys: string[],
): number | undefined => {
  const matchedKey = Object.keys(row).find((key) =>
    matcherKeys.includes(normalizeKey(key)),
  )

  if (!matchedKey) {
    return undefined
  }

  const value = row[matchedKey]
  if (value === undefined || value === null || value === '') {
    return undefined
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : undefined
}

const BulkProductUpload: React.FC<BulkProductUploadProps> = ({
  parsedProducts = [],
  onProductsParsed,
  onClose,
}) => {
  const { showFailure, showSuccess, showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [localProducts, setLocalProducts] =
    React.useState<BulkProductUploadResult[]>(parsedProducts)
  const [fileName, setFileName] = React.useState('')
  const [error, setError] = React.useState('')
  const [isDragging, setIsDragging] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)

  React.useEffect(() => {
    setLocalProducts(parsedProducts)
  }, [parsedProducts])

  const handleDownloadTemplate = () => {
    const templateRows = [
      {
        'Product SKU': 'SKU-001',
        'Product Name': 'Sample Product',
        'Product Image': 'https://example.com/sample.jpg',
        'Product Type': 'FG',
        'Stock Count': 0,
        Price: 99.99,
      },
      {
        'Product SKU': '',
        'Product Name': '',
        'Product Image': '',
        'Product Type': '',
        'Stock Count': '',
        Price: '',
      },
    ]

    const worksheet = XLSX.utils.json_to_sheet(templateRows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Products')
    XLSX.writeFile(workbook, 'product_upload_template.xlsx')
  }

  const extractValue = (row: SheetRow, matcherKeys: string[]): string => {
    const matchedKey = Object.keys(row).find((key) =>
      matcherKeys.includes(normalizeKey(key)),
    )

    if (!matchedKey) {
      return ''
    }

    const value = row[matchedKey]
    if (value === undefined || value === null) {
      return ''
    }

    return value.toString().trim()
  }

  const mapRowsToProducts = (rows: SheetRow[]): BulkProductUploadResult[] => {
    return rows
      .map((row) => {
        const productSku = extractValue(row, ['productsku', 'sku'])
        const productName = extractValue(row, ['productname', 'name'])
        const productImage = extractValue(row, [
          'productimage',
          'image',
          'imageurl',
        ])
        const productType = extractValue(row, ['producttype', 'type'])
        const stockCount = extractNumber(row, [
          'stockcount',
          'stock',
          'inventorycount',
          'availableunits',
        ])
        const price = extractNumber(row, ['price', 'productprice', 'unitprice'])

        return {
          productSku,
          productName,
          productImage,
          productType,
          stockCount,
          price,
        }
      })
      .filter((item) => item.productSku && item.productName)
  }

  const processFile = (file: File) => {
    const isAccepted =
      ACCEPTED_MIME_TYPES.includes(file.type) ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.xls') ||
      file.name.endsWith('.xlsx')

    if (!isAccepted) {
      setError('Please upload a CSV or Excel file (.csv, .xls, .xlsx).')
      showFailure('Unsupported file type')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const buffer = event.target?.result
        if (!buffer) {
          throw new Error('Unable to read file')
        }

        const data = new Uint8Array(buffer as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json<SheetRow>(worksheet, {
          defval: '',
        })

        if (jsonData.length === 0) {
          throw new Error('Uploaded sheet is empty.')
        }

        const products = mapRowsToProducts(jsonData)

        if (products.length === 0) {
          throw new Error('No valid rows found. Please follow the template.')
        }

        setLocalProducts(products)
        setFileName(file.name)
        setError('')
        onProductsParsed?.(products)
        showSuccess(`Parsed ${products.length} products successfully`)
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Unable to process the file'
        setError(message)
        setLocalProducts([])
        onProductsParsed?.([])
        showFailure(message)
      }
    }

    reader.readAsArrayBuffer(file)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0]
    if (!uploadedFile) {
      return
    }
    processFile(uploadedFile)
    event.target.value = ''
  }

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(false)
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      processFile(event.dataTransfer.files[0])
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleReset = () => {
    setFileName('')
    setLocalProducts([])
    setError('')
    onProductsParsed?.([])
    fileInputRef.current && (fileInputRef.current.value = '')
  }

  const handleProceed = () => {
    if (!localProducts.length) {
      showFailure('Please upload and parse a sheet before proceeding.')
      return
    }

    setIsSubmitting(true)
    showPageLoader?.()

    const handleSuccess = (res: AjaxResponse<BulkUploadResponse>) => {
      // Check if the response contains an error
      if (res.response?.error) {
        showFailure(res.response.error)
        setIsSubmitting(false)
        hidePageLoader?.()
        return
      }

      const data = res.response?.data as
        | {
            created?: number
            failed?: number
            total?: number
            errors?: string[]
          }
        | undefined

      let message =
        res.response?.message || 'Product sheet submitted successfully.'

      if (data) {
        const { created = 0, failed = 0, total = 0, errors = [] } = data
        const details: string[] = []

        if (total > 0) {
          details.push(`Total: ${total}`)
        }
        if (created > 0) {
          details.push(`Created: ${created}`)
        }
        if (failed > 0) {
          details.push(`Failed: ${failed}`)
        }
        if (errors.length > 0) {
          details.push(`Errors: ${errors.length}`)
        }

        if (details.length > 0) {
          message = `${message} (${details.join(', ')})`
        }
      }

      showSuccess(message)
      setIsSubmitting(false)
      hidePageLoader?.()
      // Close the slider after successful upload
      onClose?.()
    }

    const handleError = (err: AjaxError) => {
      console.error('bulk-product-upload:submit', err)
      showFailure('Unable to submit product sheet.')
      setIsSubmitting(false)
      hidePageLoader?.()
    }

    submitBulkProductUpload(localProducts).subscribe({
      next: handleSuccess,
      error: handleError,
    })
  }

  return (
    <DcFlexBox
      flexDirection="column"
      padding="24px"
      gap="20px"
      height="100%"
      style={{ overflowY: 'auto' }}
    >
      <DcDiv>
        <DcText style={{ fontSize: '14px', color: '#5c6272' }}>
          Download the &nbsp;
          <a
            style={{ color: colors.primary, cursor: 'pointer' }}
            onClick={handleDownloadTemplate}
            target="_blank"
          >
            template
          </a>
          , add your products with the required columns, and upload the sheet
          back. We currently require <b>Product SKU</b>, <b>Product Name</b>,
          <b>Product Image</b>, <b>Product Type</b>, <b>Stock Count</b>, and{' '}
          <b>Price</b>.
        </DcText>
      </DcDiv>

      <DcFlexBox height="auto" gap="10px">
        <DcButton
          variant="link"
          size="sm"
          disabled={!fileName && localProducts.length === 0}
          onClick={handleReset}
        >
          <PixelIcon color="#5c6272" icon={faRotateLeft} />
          &nbsp;Reset
        </DcButton>
      </DcFlexBox>

      <DropZone
        $isDragging={isDragging}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        htmlFor="bulk-product-upload-input"
      >
        <PixelIcon color="#5c4dff" icon={faFileArrowUp} />
        <DcText style={{ fontWeight: 600 }}>Drag & drop your sheet</DcText>
        <DcText style={{ fontSize: '13px', color: '#7a7f92' }}>
          or click to select a file
        </DcText>
        <DcText style={{ fontSize: '12px', color: '#9aa0b5' }}>
          Accepted formats: .csv, .xls, .xlsx
        </DcText>
        {fileName && (
          <DcText style={{ fontSize: '13px', color: '#4a5568' }}>
            Selected file: <b>{fileName}</b>
          </DcText>
        )}
        {error && <ErrorText>{error}</ErrorText>}
        <input
          id="bulk-product-upload-input"
          type="file"
          accept=".csv,.xls,.xlsx"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </DropZone>

      {localProducts.length > 0 && (
        <>
          <DcDiv>
            <DcText style={{ fontSize: '15px', fontWeight: 600 }}>
              Parsed products ({localProducts.length})
            </DcText>
            <DcText style={{ fontSize: '12px', color: '#7a7f92' }}>
              Below preview only shows first 10 rows to keep the slider light.
            </DcText>
          </DcDiv>
          <PreviewList>
            {localProducts.slice(0, 10).map((product, index) => (
              <PreviewRow key={`${product.productSku}-${index}`}>
                <DcText style={{ fontWeight: 600 }}>
                  {product.productName}
                </DcText>
                <DcText style={{ fontSize: '13px', color: '#7a7f92' }}>
                  SKU: {product.productSku}
                </DcText>
                {product.productImage && (
                  <DcText style={{ fontSize: '12px', color: '#9aa0b5' }}>
                    Image: {product.productImage}
                  </DcText>
                )}
                {product.productType && (
                  <DcText style={{ fontSize: '12px', color: '#9aa0b5' }}>
                    Type: {product.productType}
                  </DcText>
                )}
                {typeof product.stockCount === 'number' && (
                  <DcText style={{ fontSize: '12px', color: '#9aa0b5' }}>
                    Stock: {product.stockCount}
                  </DcText>
                )}
                {typeof product.price === 'number' && (
                  <DcText style={{ fontSize: '12px', color: '#9aa0b5' }}>
                    Price: {product.price}
                  </DcText>
                )}
              </PreviewRow>
            ))}
          </PreviewList>
          <DcFlexBox height="auto" justifyContent="flex-end">
            <DcButton
              variant="primary"
              size="sm"
              onClick={handleProceed}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting…' : 'Proceed'}
            </DcButton>
          </DcFlexBox>
        </>
      )}
    </DcFlexBox>
  )
}

export default BulkProductUpload
